import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../components/hooks/useTableSortInit';
import SummaryTiles, { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import UserAgentCell from '../../../../components/utils/UserAgentCell';
import Copyable from '../../../../components/utils/Copyable';
import TimeAgo from '../../../../components/utils/TimeAgo';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import {
  GetUserSessionsBIZidentity
} from '../../../../redux/actions/business/identity/userSessionsActions';

const UserSessionsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.identity"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [sessionsList, setSessionsList] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [summary, setSummary] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // narrowing the result set while on a later page would show an empty table
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "User", sort: "user_id" },
    { label: "IP address", sort: "ip" },
    { label: "Device", sort: null },
    { label: "Last seen", sort: "last_seen_at" },
    { label: "Status", sort: "revoked_at" },
    { label: "Signed in", sort: "createdAt" },
  ];

  useEffect(() => {
    handleGetSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection, statusFilter]);

  useEffect(() => {
    try {
      setSessionsList(state.userSessionsList?.records || []);
      setSessionsTotal(state.userSessionsList?.total || 0);
      setSummary(state.userSessionsList?.summary || null);
    } catch (err) {}
  }, [state]);

  async function handleGetSessions(customLoading){
    let query = "";
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    if(statusFilter) query += `&status=${statusFilter}`;
    await dispatch(GetUserSessionsBIZidentity(page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const summaryTiles = [
    { label: "Sessions", value: Number(summary?.total || 0), accent: TILE_ACCENT.neutral },
    { label: "Active", value: Number(summary?.active || 0), accent: TILE_ACCENT.good },
    { label: "Revoked", value: Number(summary?.revoked || 0), accent: TILE_ACCENT.bad },
  ];

  return (
    <Box>

      <TopBar
        title={"User Sessions"}
        search={search}
        searchPlaceholder={"Search id, user id, IP or device"}
        onSearchChange={handleSearchChange}
        button1Text={"Refresh"}
        button1OnClick={() => {
          handleGetSessions();
        }}
        />

      <SummaryTiles tiles={summaryTiles} />

      <Box className='flexCenter_Row' flexWrap={"wrap"} gap={"12px"} mb={"14px"}>
        <TextField
          select
          size='small'
          label='Status'
          sx={{ minWidth: "160px" }}
          value={statusFilter}
          onChange={handleStatusChange}>
          <MenuItem value=''>All</MenuItem>
          <MenuItem value='active'>Active</MenuItem>
          <MenuItem value='revoked'>Revoked</MenuItem>
        </TextField>
      </Box>

      <Typography
        variant='caption'
        sx={{ color: "grey", display: "block", mb: "14px" }}>
        The session token identifier (jti) is a bearer secret and is never
        returned by the API, so it cannot be read from this screen.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          sessionsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No session found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                sessionsList.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      // a revoked session is dimmed so live sessions stand out
                      opacity: row.revoked_at ? 0.55 : 1,
                    }}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography>
                          {row?.user?.first_name || <NA />}&nbsp;{row?.user?.last_name || ""}
                        </Typography>
                        <i><Typography variant='caption'>{row?.user?.primary_email || <NA />}</Typography></i>
                      </Box>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.user_id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Copyable>
                        {row.ip || <NA />}
                      </Copyable>
                    </TableCell>
                    <TableCell>
                      <UserAgentCell userAgent={row.user_agent} />
                    </TableCell>
                    <TableCell>
                      <TimeAgo value={row.last_seen_at} />
                    </TableCell>
                    <TableCell>
                      {
                        row.revoked_at
                        ?
                        <Tooltip title={row.revoked_reason ? `Reason: ${row.revoked_reason}` : "Revoked"}>
                          <Box>
                            <Chip size='small' label="Revoked" color="error" variant='filled' />
                            <Typography variant='caption' color={"grey"} sx={{ display: "block" }}>
                              {row.revoked_reason || ""}
                            </Typography>
                          </Box>
                        </Tooltip>
                        :
                        <Chip size='small' label="Active" color="success" variant='filled' />
                      }
                    </TableCell>
                    <TableCell>
                      <TimeAgo value={row.createdAt} color={"grey"} />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={sessionsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default UserSessionsPage;
